import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail } from "../../shared/types/api";
import {
  Button,
  DateTimeField,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
} from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * A short list of zones, plus whatever the release already uses.
 *
 * <p>Not the full IANA list. A release window is agreed with a team that sits somewhere, and
 * offering six hundred zones to pick one of four makes the common case slower for no benefit.
 */
const ZONES = [
  "Asia/Kolkata",
  "Europe/London",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/Berlin",
  "Australia/Sydney",
  "UTC",
];

/**
 * When the release goes out, and the window it may go out in (§17).
 *
 * <p>Times are stored as absolute instants and entered as wall-clock time in a named zone. A
 * deployment window agreed as "10 PM Friday" means 10 PM somewhere, and two people in two
 * countries reading a zone-less time is how somebody joins a call an hour late.
 *
 * <p>The window is separate from the release date on purpose. The date is when it is expected to
 * go; the window is the period during which it is permitted to — a change-managed environment
 * often allows deployments only between certain hours, and the deployment-window gate checks the
 * second, not the first.
 */
export function ScheduleReleaseDrawer({
  release,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [timeZone, setTimeZone] = useState(release.timeZone ?? "UTC");
  const [plannedStartAt, setPlannedStartAt] = useState<string | null>(null);
  const [plannedReleaseAt, setPlannedReleaseAt] = useState<string | null>(null);
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const [windowEnd, setWindowEnd] = useState<string | null>(null);
  const [downtime, setDowntime] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTimeZone(
      release.timeZone
        ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        ?? "UTC",
    );
    setPlannedStartAt(release.plannedStartAt ?? null);
    setPlannedReleaseAt(release.plannedReleaseAt ?? null);
    setWindowStart(release.deploymentWindowStart ?? null);
    setWindowEnd(release.deploymentWindowEnd ?? null);
    setDowntime(
      release.plannedDowntimeMinutes != null ? String(release.plannedDowntimeMinutes) : "",
    );
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, release]);

  /*
   * Caught here as well as on the server, because the message is more useful next to the field
   * that is wrong than as a banner after a failed save.
   */
  const windowBackwards =
    windowStart != null && windowEnd != null && new Date(windowEnd) <= new Date(windowStart);
  const releaseOutsideWindow =
    plannedReleaseAt != null
    && windowStart != null
    && windowEnd != null
    && !windowBackwards
    && (new Date(plannedReleaseAt) < new Date(windowStart)
      || new Date(plannedReleaseAt) > new Date(windowEnd));

  const submit = () =>
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/schedule`, {
          plannedStartAt,
          plannedReleaseAt,
          deploymentWindowStart: windowStart,
          deploymentWindowEnd: windowEnd,
          timeZone,
          plannedDowntimeMinutes: downtime === "" ? null : Number(downtime),
          lockVersion: release.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="When does this go out?"
      subtitle={`${release.version} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={windowBackwards} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field
          label="Time zone"
          required
          hint="The zone these times are written in. Stored as absolute instants, so everybody sees the same moment."
        >
          <Select value={timeZone} onChange={(event) => setTimeZone(event.target.value)}>
            {[...new Set([timeZone, ...ZONES])].map((zone) => (
              <option key={zone} value={zone}>
                {zone.replace(/_/g, " ")}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Planned release"
          hint="When it is expected to go out. The deployment-window gate needs this."
        >
          <DateTimeField
            value={plannedReleaseAt}
            onChange={setPlannedReleaseAt}
            timeZone={timeZone}
          />
        </Field>

        <Field label="Planned start" hint="When work on the deployment begins, if that differs.">
          <DateTimeField
            value={plannedStartAt}
            onChange={setPlannedStartAt}
            timeZone={timeZone}
          />
        </Field>

        <div className="rounded-lg border border-ink-200 p-3">
          <p className="text-sm font-medium text-ink-900">Deployment window</p>
          <p className="mt-0.5 text-xs text-ink-600">
            The period this release is permitted to go out in. Leave both blank if there is no
            restriction — the gate then only checks that a date exists.
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Opens">
              <DateTimeField value={windowStart} onChange={setWindowStart} timeZone={timeZone} />
            </Field>
            <Field label="Closes">
              <DateTimeField value={windowEnd} onChange={setWindowEnd} timeZone={timeZone} />
            </Field>
          </div>

          {windowBackwards && (
            <InlineAlert tone="error">The window closes before it opens.</InlineAlert>
          )}
          {releaseOutsideWindow && (
            <InlineAlert tone="warning">
              The planned release is outside the window. That is allowed — but the deployment
              window gate will hold the release when the time comes.
            </InlineAlert>
          )}
        </div>

        <Field
          label="Planned downtime (minutes)"
          hint="How long the service is expected to be unavailable. Zero if there is none."
        >
          <Input
            type="number"
            min={0}
            value={downtime}
            onChange={(event) => setDowntime(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
